/**
 * Nav menu item — consumed by sidebar / topbar partials.
 *
 * `href` is a route NAME (Vue Router named route), not a path —
 * resolves via `<router-link :to="{ name: ... }">` so route refactors
 * don't break menus.
 *
 * `current` is computed per-route by the menu source store
 * (`uiStore.menuItems`); consumers don't compute active-state.
 */
export interface MenuItem {
  /** Display label. */
  name: string
  /** Named-route target (Vue Router). */
  href: string
  /** True when current route is this item (or a descendant). */
  current: boolean
  /** PrimeIcons class fragment, e.g. `'pi-th-large'`. */
  icon?: string
  /** Required role(s) — item is hidden if user lacks any. */
  roles?: string[]
  /** Required permission(s) — item hidden if user lacks any. */
  permissions?: string[]
  /** Nested items — render as expandable group. */
  children?: MenuItem[]
  /** Small uppercase chip rendered after the label (e.g. "Demo"). */
  chip?: string
  /** Optional query params for the route target (e.g. `{ view: 'loss' }`). */
  query?: Record<string, string>
}

/**
 * Sentinel entry rendered as a horizontal rule between groups of
 * menu items. Carries no navigation / label / permission state.
 */
export interface MenuDivider {
  divider: true
}

/** Union of all entries the sidebar can iterate over. */
export type MenuEntry = MenuItem | MenuDivider

/** Type guard: narrows a `MenuEntry` to `MenuDivider`. */
export function isMenuDivider(e: MenuEntry): e is MenuDivider {
  return 'divider' in e
}
